import React, { useEffect, useState, useContext } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Divider,
  Grid,
  Autocomplete,
  TextField
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteIcon from '@mui/icons-material/Delete'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { authContext } from '../../../contexts/AuthContext'
import {
  listSocietyComplianceDocuments,
  uploadSocietyComplianceDocument,
  deleteSocietyComplianceDocument,
  generateSocietyComplianceDocumentUrl,
  linkClientDocumentToCompliance,
  listClientDocumentsForSociety,
  unlinkSocietyComplianceDocument
} from '../../../services/societyComplianceDocuments'
import DeleteDialog from '../../DeleteDialog'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { DocumentViewer } from '../../Utils/DocumentViewer'

export default function SocietyComplianceDocuments({
  open,
  onClose,
  compliance,
  society,
  adminMode = false
}) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const [documents, setDocuments] = useState([])
  const [file, setFile] = useState(null)
  const [deleteItem, setDeleteItem] = useState({})
  const [viewerDoc, setViewerDoc] = useState(null)
  const [openLinkDialog, setOpenLinkDialog] = useState(false)
  const [clientDocuments, setClientDocuments] = useState([])
  const [selectedClientDoc, setSelectedClientDoc] = useState(null)

  useEffect(() => {
    if (open && compliance?.id) fetchDocuments()
  }, [open, compliance])

  const fetchDocuments = async () => {
    try {
      const data = await listSocietyComplianceDocuments(compliance.id)
      setDocuments(data)
    } catch (err) {
      console.error('Error loading compliance documents:', err)
    }
  }

  const fetchClientDocuments = async () => {
    try {
      const res = await listClientDocumentsForSociety(society.id)
      setClientDocuments(res || [])
    } catch (err) {
      console.error('Error loading client documents:', err)
    }
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null)
  }

  const handleUpload = async () => {
    if (!file) {
      console.error('Missing file')
      return
    }

    if (!compliance?.id) {
      console.error('Missing compliance id')
      return
    }
    try {
      await uploadSocietyComplianceDocument({
        file,
        societyLegalId: society.legal_id,
        complianceSocietyId: compliance.id,
        complianceId: compliance.id,
        loggedUserId: auth.user.id
      })
      await fetchDocuments()
      setFile(null)
    } catch (err) {
      console.error('Error uploading file:', err)
    }
  }

  const handlePreview = async (doc) => {
    try {
      if (!doc) return
      const res = await generateSocietyComplianceDocumentUrl(doc.id)
      if (res?.url) {
        const filename = doc.is_linked ? doc.linked_filename : doc.file_name
        setViewerDoc({ uri: res.url, filename })
      }
    } catch (err) {
      console.error('Error previewing document:', err)
    }
  }

  const handleDownload = async (doc) => {
    try {
      if (!doc) return
      const res = await generateSocietyComplianceDocumentUrl(doc.id)
      if (res?.url) {
        const response = await fetch(res.url)
        const blob = await response.blob()

        const fileName = doc.is_linked
          ? doc.linked_filename
          : doc.file_name || 'document.pdf'

        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000)
      }
    } catch (err) {
      console.error('Error downloading document:', err)
    }
  }

  const handleDelete = async (item) => {
    try {
      if (item.is_linked) {
        await unlinkSocietyComplianceDocument(item.id, auth.user.id)
      } else {
        await deleteSocietyComplianceDocument(item.id, auth.user.id)
      }

      await fetchDocuments()
    } catch (err) {
      console.error('Error deleting document:', err)
    }

    setDeleteItem({})
  }

  const handleLink = async () => {
    if (!selectedClientDoc) return

    try {
      await linkClientDocumentToCompliance({
        complianceSocietyId: compliance.id,
        clientDocumentId: selectedClientDoc.id,
        loggedUserId: auth.user.id
      })

      await fetchDocuments()
      setSelectedClientDoc(null)
      setOpenLinkDialog(false)
    } catch (err) {
      console.error('Error linking document:', err)
    }
  }

  const onCloseDialog = () => {
    setDeleteItem({})
    setViewerDoc(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onCloseDialog} maxWidth='md' fullWidth>
      <DialogTitle>
        {t('Documents for')}{' '}
        <strong>
          {society.language === 'es'
            ? compliance?.name_es
            : compliance?.name_en}
        </strong>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={viewerDoc ? 6 : 12}>
            {documents.length === 0 ? (
              <Typography color='text.secondary'>
                {t('No documents found')}
              </Typography>
            ) : (
              <List dense>
                {documents.map((doc) => (
                  <React.Fragment key={doc.id}>
                    <ListItem
                      button
                      onClick={() => handlePreview(doc)}
                      secondaryAction={
                        <>
                          <IconButton
                            edge='end'
                            aria-label='download'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDownload(doc)
                            }}
                          >
                            <FileDownloadIcon color='secondary' />
                          </IconButton>

                          {adminMode && (
                            <IconButton
                              edge='end'
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteItem(doc)
                              }}
                              aria-label='delete'
                            >
                              <DeleteIcon color='error' />
                            </IconButton>
                          )}
                        </>
                      }
                    >
                      <PictureAsPdfIcon color='primary' sx={{ mr: 1 }} />

                      <ListItemText
                        primary={
                          <span
                            style={{
                              cursor: 'pointer'
                            }}
                            onClick={() => handlePreview(doc)}
                          >
                            {doc.is_linked
                              ? doc.linked_filename
                              : doc.file_name}
                          </span>
                        }
                        secondary={`${t('Uploaded at')}: ${new Date(
                          doc.uploaded_at
                        ).toLocaleDateString()}`}
                      />
                    </ListItem>

                    <Divider component='li' />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Grid>

          {viewerDoc && (
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  height: '70vh',
                  overflow: 'hidden'
                }}
              >
                <DocumentViewer
                  fileUrl={viewerDoc.uri}
                  extension={
                    viewerDoc.filename &&
                    viewerDoc.filename.substring(
                      viewerDoc.filename.lastIndexOf('.')
                    )
                  }
                  height='100%'
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          {adminMode && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component='label'
                variant='outlined'
                startIcon={<UploadFileIcon />}
              >
                {t('Upload new')}
                <input
                  key={file ? file.name : 'empty'}
                  type='file'
                  hidden
                  accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  setOpenLinkDialog(true)
                  fetchClientDocuments()
                }}
              >
                {t('Associate existing document')}
              </Button>
            </Box>
          )}

          {file && (
            <Box
              sx={{
                p: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#fafafa'
              }}
            >
              <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                {t('Selected file')}: <strong>{file.name}</strong>
              </Typography>
              <Button size='small' color='error' onClick={() => setFile(null)}>
                {t('Discard')}
              </Button>
            </Box>
          )}

          {adminMode && (
            <Button
              variant='contained'
              onClick={handleUpload}
              disabled={!file}
              sx={{
                backgroundColor:
                  window.$brand === 'PRB' ? '#17afb6' : '#102a46',
                borderRadius: window.$brand === 'PRB' ? 0 : 5
              }}
            >
              {t('Save')}
            </Button>
          )}
        </Box>
      </DialogActions>

      <DeleteDialog
        item={deleteItem}
        title={
          deleteItem?.is_linked
            ? t('unlink_document_title')
            : t('delete_document')
        }
        caption={
          deleteItem?.is_linked
            ? t('unlink_document_caption')
            : t('delete_document_full_caption')
        }
        targetFunc={handleDelete}
        handleDialogClose={() => setDeleteItem({})}
      />

      <Dialog
        open={openLinkDialog}
        onClose={() => setOpenLinkDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{t('Associate existing document')}</DialogTitle>

        <DialogContent>
          {clientDocuments.length === 0 ? (
            <Typography color='text.secondary'>
              {t('No client documents available')}
            </Typography>
          ) : (
            <Autocomplete
              options={clientDocuments}
              getOptionLabel={(opt) => opt.filename}
              onChange={(e, val) => setSelectedClientDoc(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('Select document')}
                  variant='standard'
                />
              )}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenLinkDialog(false)}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={!selectedClientDoc}
            variant='contained'
            onClick={handleLink}
            sx={{
              backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
              borderRadius: window.$brand === 'PRB' ? 0 : 5
            }}
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
